import { createSelector } from 'reselect';
import { Route } from 'app/shared/utils/routes';
import {
  CustomConfigType,
  CustomDataConfigLabels,
  CustomInterfaceSettings,
  CustomTableConfig,
  TableConfigActionTypes,
  TableConfigType,
} from 'app/shared/CustomConfig/models';
import { selectAvailableHomeRoutes } from 'app/modules/session/selectors';
import { FeatureFlag } from 'app/shared/featureFlags/models';
import { selectFeatureFlags } from 'app/shared/featureFlags/selectors';
import {
  buildDataSettingColumn,
  buildNonCustomDataTableColumn,
  getTableConfigs,
} from 'app/shared/CustomConfig/helpers';
import { selectMergedTableSettingsByClassifier } from 'app/modules/tableSettings/selectors';
import {
  OrgTableColumnKeyType,
  OrgTableConfig,
} from 'app/modules/tableSettings/responses';
import { CLASSIFIER_TO_LABEL_MAP } from 'app/modules/tableSettings/constants';
import {
  OrgDataSettingsConfig,
  Unit21DataClassifier,
} from 'app/modules/dataSettings/responses';
import { TableConfig } from 'app/shared/pagination/models';
import {
  selectCustomDataTableLabelsByClassifier,
  selectDataSettingsById,
} from 'app/modules/dataSettings/selectors';
import { QueueType } from 'app/modules/queues/models';
import {
  AI_RECOMMENDATION_COLUMN,
  ALERT_SCORE_COLUMN_ID,
} from 'app/modules/alerts/columns';
import { selectEnrichmentConfig } from 'app/modules/orgSettings/selectors';

export const selectCustomConfigs = (state: RootState) =>
  state.tableConfig.configs;

export const selectCustomConfigsLoading = (state: RootState) => {
  return Boolean(
    state.loading[TableConfigActionTypes.RETRIEVE_ORG_TABLE_CONFIG],
  );
};

export const saveCustomConfigsLoading = (state: RootState) => {
  return Boolean(state.loading[TableConfigActionTypes.SAVE_TABLE_CONFIG]);
};

export const selectTableConfigs = createSelector(
  selectCustomConfigs,
  (configs) =>
    getTableConfigs(configs) as Record<TableConfigType, CustomTableConfig>,
);

export const selectCustomDataConfigs = createSelector(
  selectCustomConfigs,
  (configs) =>
    Object.keys(configs).reduce((acc, key) => {
      if (CustomDataConfigLabels[key]) {
        acc[key] = configs[key];
      }
      return acc;
    }, {}),
);

export const convertTableConfig = (
  { columns, type_classifier: classifier }: OrgTableConfig,
  dataSettingsById: Record<number, OrgDataSettingsConfig | undefined> | null,
): TableConfig[] => {
  return columns.reduce<TableConfig[]>((acc, column) => {
    if (column.key_type === OrgTableColumnKeyType.DATA_SETTING) {
      // shouldn't be possible but
      if (!dataSettingsById?.[column.data_setting_id]) {
        return acc;
      }
      const dataSetting = dataSettingsById[column.data_setting_id];
      if (dataSetting) {
        acc.push(buildDataSettingColumn(dataSetting));
      }
      return acc;
    }
    const label =
      CLASSIFIER_TO_LABEL_MAP[classifier][column.key] || 'Unknown Column';
    acc.push(buildNonCustomDataTableColumn(column.key, label));
    return acc;
  }, []);
};

export const selectEntityTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  (tableSettings, dataSettingsById) => {
    const entitySettings =
      tableSettings && tableSettings[Unit21DataClassifier.ENTITY];
    return entitySettings
      ? convertTableConfig(entitySettings, dataSettingsById)
      : [];
  },
);

export const selectInstrumentTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  (tableSettings, dataSettingsById) => {
    const instrumentSettings =
      tableSettings && tableSettings[Unit21DataClassifier.INSTRUMENT];
    return instrumentSettings
      ? convertTableConfig(instrumentSettings, dataSettingsById)
      : [];
  },
);

export const selectTxnTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  selectEnrichmentConfig,
  (tableSettings, dataSettingsById, enrichmentConfig) => {
    const eventSettings =
      tableSettings && tableSettings[Unit21DataClassifier.EVENT];
    const config = eventSettings
      ? convertTableConfig(eventSettings, dataSettingsById)
      : [];

    if (!enrichmentConfig.fingerprint?.enabled) {
      return config.filter((c) => c.key !== 'device_intelligence');
    }
    return config;
  },
);

export const selectActionEventTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  selectEnrichmentConfig,
  (tableSettings, dataSettingsById, enrichmentConfig) => {
    const actionEventTableSettings =
      tableSettings && tableSettings[Unit21DataClassifier.ACTION_EVENT];
    const config = actionEventTableSettings
      ? convertTableConfig(actionEventTableSettings, dataSettingsById)
      : [];

    if (!enrichmentConfig.fingerprint?.enabled) {
      return config.filter((c) => c.key !== 'device_intelligence');
    }
    return config;
  },
);

export const selectAlertTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  selectFeatureFlags,
  (tableSettings, dataSettingsById, featureFlags) => {
    const alertSettings =
      tableSettings && tableSettings[Unit21DataClassifier.ALERT];
    return alertSettings
      ? convertTableConfig(alertSettings, dataSettingsById).filter((config) => {
          if (config.key === ALERT_SCORE_COLUMN_ID) {
            return false;
          }
          if (config.key === AI_RECOMMENDATION_COLUMN.id) {
            return featureFlags[FeatureFlag.AI_DECISIONING_V0];
          }
          return true;
        })
      : [];
  },
);

export const selectCaseTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  (tableSettings) => {
    const caseSettings =
      tableSettings && tableSettings[Unit21DataClassifier.CASE];
    return caseSettings ? convertTableConfig(caseSettings, {}) : [];
  },
);

export const selectLiveModelTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  (tableSettings) => {
    const rulesSettings =
      tableSettings && tableSettings[Unit21DataClassifier.RULE];
    return rulesSettings ? convertTableConfig(rulesSettings, {}) : [];
  },
);

export const selectFlowModelTableConfig = selectLiveModelTableConfig;

export const selectWatchlistTableConfig = createSelector(
  selectMergedTableSettingsByClassifier,
  selectDataSettingsById,
  (tableSettings, dataSettingsById) => {
    const watchlistSettings =
      tableSettings && tableSettings[Unit21DataClassifier.WATCHLIST];
    return watchlistSettings
      ? convertTableConfig(watchlistSettings, dataSettingsById)
      : [];
  },
);

export const selectAiAdminTrackingTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.AI_ADMIN_TRACKING_TABLE]
    ?.definition || [];

export const selectFilingsTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.SAR_TABLE]?.definition || [];

export const selectAlertQueuesTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.ALERT_QUEUES_TABLE]?.definition ||
  [];

export const selectCaseQueuesTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.CASE_QUEUES_TABLE]?.definition ||
  [];

export const selectSarQueuesTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.SAR_QUEUES_TABLE]?.definition || [];

export const selectDataSettingsTableConfig = (state: RootState) =>
  state.tableConfig.configs[TableConfigType.DATA_SETTINGS]?.definition || [];

export const selectDashboardSettings = (
  state: RootState,
): CustomInterfaceSettings =>
  (state.tableConfig.configs[CustomConfigType.INTERFACE_SETTINGS]
    ?.definition as CustomInterfaceSettings) || {};

export const selectCustomHomePage = createSelector(
  selectDashboardSettings,
  selectAvailableHomeRoutes,
  (settings, availableHomeRoutes): Route | undefined => {
    // Returns the custom home page if the user defined one and is available for them (have right permissions)
    if (settings.homePage) {
      return availableHomeRoutes.find(
        (route) => route.id === settings.homePage,
      );
    }
    return undefined;
  },
);

export const selectEntityCustomDataSettings = createSelector(
  selectCustomConfigs,
  (configs) =>
    configs[CustomConfigType.ENTITY_CUSTOM_DATA_SETTINGS]?.definition || [],
);

export const selectTxnCustomDataSettings = createSelector(
  selectCustomConfigs,
  (configs) =>
    configs[CustomConfigType.TXN_CUSTOM_DATA_SETTINGS]?.definition || [],
);

export const selectInstrumentCustomDataSettings = createSelector(
  selectCustomConfigs,
  (configs) =>
    configs[CustomConfigType.INSTRUMENT_CUSTOM_DATA_SETTINGS]?.definition || [],
);

export const selectEntityCustomDataKeyLabelMap = createSelector(
  selectCustomDataTableLabelsByClassifier,
  (data) => data.ENTITY,
);

export const selectTxnCustomDataKeyLabelMap = createSelector(
  selectCustomDataTableLabelsByClassifier,
  (data) => data.EVENT,
);

export const selectInstrumentCustomDataKeyLabelMap = createSelector(
  selectCustomDataTableLabelsByClassifier,
  (data) => data.INSTRUMENT,
);

export const selectAlertCustomDataKeyLabelMap = createSelector(
  selectCustomDataTableLabelsByClassifier,
  (data) => data.ALERT,
);

export const selectQueuesTableConfig = (queueType: QueueType) =>
  createSelector(
    selectAlertQueuesTableConfig,
    selectCaseQueuesTableConfig,
    selectSarQueuesTableConfig,
    (alertTableConfig, caseTableConfig, sarTableConfig) => {
      switch (queueType) {
        case QueueType.ALERT_QUEUE:
          return alertTableConfig;
        case QueueType.CASE_QUEUE:
          return caseTableConfig;
        case QueueType.SAR_QUEUE:
          return sarTableConfig;
        default:
          throw new Error(`No implementation for ${queueType}`);
      }
    },
  );
